import React, { useCallback, useContext, useState, useMemo } from 'react';
import { Col, Row, Space, Typography, Input, Badge, Spin } from 'antd';

import StatCard from '@components/StatCard';
import DeviceActionsMenu from '@components/DeviceActionsMenu';

import {
  CalendarOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import { treatSearch, treatSearchSettings } from '@utils/searchHistoryMenu';
import DeviceActionModal from '@components/DeviceActionModal';
import { getLocaleString } from '@utils/locales';
import api from '@services/api';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { useTranslation } from 'react-i18next';
import { useDeviceFormatter } from '@hooks/useDeviceFormatter';
import SessionContext from '@providers/Session/SessionContext';
import { BiBlock } from 'react-icons/bi';
import { bytesFormat, extractDateInISOFormat } from '@utils/formatters';
import CoverageMap from '@components/Maps/CoverageMap';
import Sessions from '@pages/Assets/Sessions';
import DeviceActionsTimeline from '@components/DeviceActionsTimeline';
import rolesConstants from '@utils/rolesConstants';
import RoleChecker from '@components/RoleChecker';
import { IDevice } from '@pages/Assets/Inventory/types';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import CustomCard from '@components/CustomCard';
import { Container, Content } from './styles';
import SearchHistoryMenu from './SearchHistoryMenu';

const { Paragraph, Title } = Typography;
const { Search } = Input;

const DeviceQuery: React.FC = () => {
  const { keycloak } = useContext(KeycloakContext);
  const {
    mainCustomerInContext,
    userSettings,
    setUserSettings,
    selectedApp,
    isMot,
  } = useContext(SessionContext);
  const customerId = keycloak.idTokenParsed?.customerId;

  const isMainCustomerInContext = customerId === mainCustomerInContext?.id;

  const {
    t,
    i18n: { language },
  } = useTranslation(['inventory-detailed', 'inventory']);
  const { t: tStatus } = useTranslation('status');

  const [searchMaxLength, setSearchMaxLength] = useState<number>();
  const [searchType, setSearchType] = useState('');
  const [isDeviceActionsVisible, setIsDeviceActionsVisible] =
    useState<boolean>(false);
  const [actionToExecute, setActionToExecute] = useState(-1);
  const [data, setData] = useState<IDevice>();
  const [isLoading, setIsLoading] = useState(false);
  const [isNoData, setIsNoData] = useState(false);
  const [search, setSearch] = useState<string | undefined>();

  const { device, handleUpdateNickname, handleUpdateNote } = useDeviceFormatter(
    Number(data?.id),
  );

  const isConnectOne = selectedApp === ApplicationsConstants.CONNECT_ONE;

  const searchOptions = useMemo(
    () => ['nickname', 'msisdn', 'imsi', 'iccid'],
    [],
  );

  const searchHistory = useMemo(
    () => treatSearchSettings(userSettings),
    [userSettings],
  );

  const addSearchInHistory = useCallback(
    async (value: string) => {
      const hasSearch = searchHistory.indexOf(value) !== -1;

      if (!hasSearch) {
        let callCenterSearchHistory = searchHistory
          ? [...searchHistory, value]
          : [value];

        if (callCenterSearchHistory.length > 5) {
          callCenterSearchHistory = callCenterSearchHistory.reverse();
          callCenterSearchHistory.pop();
          callCenterSearchHistory = callCenterSearchHistory.reverse();
        }

        await api.put('/users/self/settings/', {
          callCenterSearchHistory,
        });
        setUserSettings(callCenterSearchHistory);
      }
    },
    [searchHistory, setUserSettings],
  );

  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const onSearch = useCallback(
    async (value: string, type: string) => {
      if (value) {
        setIsLoading(true);
        const { data: content } = await api.post(
          `/service-proxy/inventory/${routeToFindData}`,
          {
            filters: { [type]: type === 'nickname' ? value : [value] },
          },
        );

        if (content?.content[0]) {
          if (isMainCustomerInContext) {
            addSearchInHistory(value);
          }

          const id = content?.content[0]?.id;

          const { data: deviceData } = await api.get(
            `/service-proxy/devices/${id}`,
          );

          setData({
            ...content?.content[0],
            city: deviceData?.locationInformation?.city,
            state: deviceData?.locationInformation?.state,
            statusConnected: deviceData?.statusConnected,
          });
          setIsNoData(false);
        } else {
          setIsNoData(true);
          setData(undefined);
        }
        setIsLoading(false);
      }
    },
    [addSearchInHistory, isMainCustomerInContext, routeToFindData],
  );

  const handleSearchChange = useCallback((value: string) => {
    if (!value) {
      setSearchType('');
      setSearchMaxLength(undefined);
    } else {
      const { maxLength, type } = treatSearch(value);
      setSearchType(type);
      setSearchMaxLength(maxLength);
    }
  }, []);

  const handleActionClick = useCallback((action: number) => {
    setActionToExecute(action);
    setIsDeviceActionsVisible(true);
  }, []);

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className="loading">
          <Spin style={{ background: 'transparent' }} />
        </div>
      );
    }

    if (isNoData) {
      return (
        <div style={{ textAlign: 'center' }}>
          <p>{t('device-query.device-not-found')}</p>
        </div>
      );
    }

    return (
      data && (
        <>
          <Row gutter={[16, 16]} className="actions-title">
            <Col span={24}>
              <CustomCard
                bodyStyle={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Title
                  level={4}
                  editable={{
                    onChange: handleUpdateNickname,
                  }}
                  style={{ fontWeight: 'normal', fontSize: '1rem' }}
                  disabled={!device}
                >
                  {device?.nickname}
                </Title>
                <DeviceActionsMenu
                  onActionClick={({ key }) => {
                    handleActionClick(key);
                  }}
                />
              </CustomCard>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col lg={14} xs={24}>
              <Row gutter={[16, 16]}>
                <Col sm={12} xs={24}>
                  <Space direction="vertical">
                    <StatCard
                      title={t('status')}
                      value={`${tStatus(device?.status || '')}` || '-'}
                      icon={
                        <Badge
                          status={
                            device?.status
                              ? tStatus(`badgeStatus.${device?.status}`)
                              : 'error'
                          }
                        />
                      }
                      loading={!device}
                    />
                    {isMot || isConnectOne ? (
                      <>
                        <StatCard
                          title={t('operator')}
                          icon={<PhoneOutlined />}
                          value={device?.operator || device?.operator || '-'}
                          loading={!device}
                        />
                        <StatCard
                          title={t('connected-operator')}
                          icon={<PhoneOutlined />}
                          value={device?.connectedOperator || '-'}
                          loading={!device}
                        />
                      </>
                    ) : (
                      <StatCard
                        title={
                          device?.connectedOperator
                            ? t('connected-operator')
                            : t('operator')
                        }
                        icon={<PhoneOutlined />}
                        value={
                          device?.connectedOperator || device?.operator || '-'
                        }
                        loading={!device}
                      />
                    )}

                    <StatCard
                      title={t('connection-status')}
                      icon={
                        <Badge
                          status={tStatus(
                            device?.online
                              ? 'badgeStatus.ONLINE'
                              : 'badgeStatus.OFFLINE',
                          )}
                        />
                      }
                      value={device?.online ? t('ONLINE') : t('OFFLINE')}
                      loading={!device}
                    />
                    {device?.isca && (
                      <RoleChecker
                        client="inventory"
                        role={rolesConstants.INVENT_CANCELLATION_DATE}
                      >
                        <StatCard
                          title={t('inventory:table.cancellationDate')}
                          value={
                            device?.cancellationDate
                              ? extractDateInISOFormat(device.cancellationDate)
                              : '-'
                          }
                          icon={<CalendarOutlined />}
                          loading={!device}
                        />
                      </RoleChecker>
                    )}
                  </Space>
                </Col>
                <Col sm={12} xs={24}>
                  <Space direction="vertical">
                    <StatCard
                      title={t('last-connection')}
                      value={
                        device?.lastConnection
                          ? new Date(device.lastConnection).toLocaleString(
                              getLocaleString(language),
                            )
                          : '-'
                      }
                      icon={<CalendarOutlined />}
                      loading={!device}
                    />

                    <StatCard
                      title={t('activation-date')}
                      value={
                        device?.activationDate
                          ? new Date(device.activationDate).toLocaleString(
                              getLocaleString(language),
                            )
                          : '-'
                      }
                      icon={<CalendarOutlined />}
                      loading={!device}
                    />

                    <StatCard
                      title={t('blocking-status')}
                      value={
                        `${t(
                          `inventory:lock-status.types.${
                            device?.networkBlockStatus ?? 'NONE'
                          }`,
                        )}` || '-'
                      }
                      icon={
                        <BiBlock
                          style={{
                            fontSize: '1.1rem',
                            color:
                              (device?.networkBlockStatus ?? 'NONE') === 'NONE'
                                ? 'var(--primary-color)'
                                : 'var(--red-color)',
                          }}
                        />
                      }
                      loading={!device}
                    />

                    <RoleChecker
                      client="inventory"
                      role={rolesConstants.INVENT_ISCA}
                    >
                      <StatCard
                        title={t('inventory:table.isca')}
                        value={
                          device?.isca
                            ? t('inventory:iscaOptions.yes')
                            : t('inventory:iscaOptions.no')
                        }
                        icon={<FileTextOutlined />}
                        loading={!device}
                      />
                    </RoleChecker>
                  </Space>
                </Col>
              </Row>
              <Row gutter={[116, 16]} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <StatCard
                    title={t('city')}
                    value={(() => {
                      if (device) {
                        if (device.city && device.state) {
                          return `${device.city}, ${device.state}`;
                        }
                        if (device.city) {
                          return device.city;
                        }
                        if (device.state) {
                          return device.state;
                        }
                        return '-';
                      }
                      return '-';
                    })()}
                    icon={<EnvironmentOutlined />}
                    loading={!device}
                  />
                </Col>
                <Col span={24}>
                  <StatCard
                    title={t('plan')}
                    icon={<FileTextOutlined />}
                    value={device?.plan || '-'}
                    loading={!device}
                  />
                </Col>
                <Col span={24}>
                  <StatCard
                    title={t('consumption')}
                    value={`${bytesFormat(
                      device?.consumption ?? '0',
                    )} / ${bytesFormat(device?.franchising ?? 0)}`}
                  />
                </Col>
              </Row>
            </Col>
            <Col lg={10} xs={24}>
              <Space direction="vertical">
                <CoverageMap
                  mapHeight={device?.isca ? 734 : 644}
                  dataProps={
                    device?.latitude || device?.longitude
                      ? [
                          {
                            id: `${device?.id}`,
                            lat: Number(device?.latitude || 0),
                            lng: Number(device?.longitude || 0),
                          },
                        ]
                      : undefined
                  }
                  style={{ borderRadius: 8 }}
                  allowCoverage
                />
              </Space>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col sm={12} xs={24}>
              <Space direction="vertical">
                <StatCard
                  title="ICCID"
                  value={device?.iccid}
                  groupSeparator=""
                  loading={!device}
                />
                <StatCard
                  title="IMEI"
                  value={device?.imei ?? '-'}
                  groupSeparator=""
                  loading={!device}
                />
              </Space>
            </Col>
            <Col sm={12} xs={24}>
              <Space direction="vertical">
                <StatCard
                  title="IMSI"
                  value={device?.imsi}
                  groupSeparator=""
                  loading={!device}
                />
                <StatCard
                  title="MSISDN"
                  value={device?.msisdn}
                  groupSeparator=""
                  loading={!device}
                />
              </Space>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <CustomCard
                loading={!device}
                title={t('note')}
                headStyle={{ color: 'rgba(0,0,0,0.45)' }}
              >
                <Paragraph editable={{ onChange: handleUpdateNote }}>
                  {device?.observation ?? device?.observation}
                </Paragraph>
              </CustomCard>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={24}>
              <DeviceActionsTimeline id={device?.id} />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={24}>
              <Sessions tableOnly onlyMsisdn={Number(device?.msisdn)} />
            </Col>
          </Row>
        </>
      )
    );
  }, [
    data,
    device,
    handleActionClick,
    handleUpdateNickname,
    handleUpdateNote,
    isLoading,
    isNoData,
    language,
    t,
    tStatus,
    isMot,
    isConnectOne,
  ]);

  return (
    <Container>
      <Row gutter={[16, 16]} className="search-device">
        <Col span={24}>
          <SearchHistoryMenu
            showHistory={isMainCustomerInContext}
            searchHistory={searchHistory}
            onSearchClick={(value: string) => {
              handleSearchChange(value);
              onSearch(value, treatSearch(value)?.type);
              setSearch(value);
            }}
          >
            <div className="search-device-content">
              <div className="search-type">
                {searchOptions.map((name: string) => (
                  <span
                    key={name}
                    className={searchType === name ? 'search-active' : ''}
                  >
                    {name === 'nickname' ? t('device-query.nickname') : name}
                  </span>
                ))}
              </div>
              <Search
                value={search}
                onSearch={(value: string) => {
                  onSearch(value, searchType);
                }}
                onChange={({ target: { value } }) => {
                  handleSearchChange(value);
                  setSearch(value);
                }}
                size="large"
                allowClear
                maxLength={searchMaxLength}
              />
            </div>
          </SearchHistoryMenu>
        </Col>
      </Row>

      <Content>
        {content}

        <DeviceActionModal
          visible={isDeviceActionsVisible}
          actionKey={actionToExecute}
          devicesProps={device && [device]}
          onClose={() => setIsDeviceActionsVisible(false)}
        />
      </Content>
    </Container>
  );
};

export default DeviceQuery;
